import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { 
  X, 
  Download, 
  ExternalLink,
  Maximize, 
  Minimize,
  ZoomIn,
  ZoomOut,
  RotateCw,
  FileText,
  MessageSquare,
  Send,
  Heart,
  User,
  MoreHorizontal,
  Trash,
  Edit,
  Reply,
  Loader,
  Flag
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import ProfilePicture from './ProfilePicture.jsx';

// Syntax highlighting - only import core and specific languages
import hljs from 'highlight.js/lib/core';
import javascript from 'highlight.js/lib/languages/javascript';
import css from 'highlight.js/lib/languages/css';
import xml from 'highlight.js/lib/languages/xml';
import json from 'highlight.js/lib/languages/json';
import markdown from 'highlight.js/lib/languages/markdown';
import 'highlight.js/styles/github.css';

// Register only the languages we need
hljs.registerLanguage('javascript', javascript);
hljs.registerLanguage('css', css);
hljs.registerLanguage('html', xml);
hljs.registerLanguage('xml', xml);
hljs.registerLanguage('json', json);
hljs.registerLanguage('markdown', markdown);

// Lazy-loaded components for different file types
const PdfViewer = lazy(() => import('./preview/PdfViewer'));
const ImageViewer = lazy(() => import('./preview/ImageViewer'));
const TextViewer = lazy(() => import('./preview/TextViewer'));
const CsvViewer = lazy(() => import('./preview/CsvViewer'));
const MarkdownViewer = lazy(() => import('./preview/MarkdownViewer'));
const DocxViewer = lazy(() => import('./preview/DocxViewer'));

// Loading fallback
const LoadingFallback = () => (
  <div className="flex items-center justify-center h-full">
    <Loader className="h-10 w-10 text-red-500 animate-spin" />
  </div>
);

const DocumentViewer = ({ 
  onClose, 
  title,
  fileType,
  fileUrl,
  token,
  materialId,
  currentUser,
  sidebarWidth = 256,
  isFullScreenPage = false // New prop to indicate if it's used in a full-screen page
}) => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [showComments, setShowComments] = useState(true);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isPostingComment, setIsPostingComment] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [editingComment, setEditingComment] = useState(null);
  const [editText, setEditText] = useState('');
  const [fileError, setFileError] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  
  const commentsEndRef = useRef(null);
  const commentInputRef = useRef(null);
  const apiBaseUrl = process.env.REACT_APP_API_URL || window.location.origin;

  // Check if current user is moderator or admin
  const canModerate = currentUser && (currentUser.role === 'moderator' || currentUser.role === 'admin');

  // Toggle fullscreen mode
  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
  };

  // Change zoom level
  const handleZoom = (direction) => {
    if (direction === 'in' && zoom < 2) {
      setZoom(zoom + 0.25);
    } else if (direction === 'out' && zoom > 0.5) {
      setZoom(zoom - 0.25);
    }
  };

  // Rotate the document
  const handleRotate = () => {
    setRotation((rotation + 90) % 360);
  };

  // Simplify and standardize the file URL construction
  const getProperFileUrl = () => {
    // Safety check for undefined fileUrl
    if (!fileUrl) {
      console.error('fileUrl is undefined in getProperFileUrl');
      return '';
    }
    
    // If it's already a fully formed URL with our API base, just return it
    if (fileUrl.includes('/api/files/') && fileUrl.startsWith(apiBaseUrl)) {
      return fileUrl;
    }
    
    let filePath = "";
    
    // If it's a relative API path
    if (fileUrl.startsWith('/api/files/')) {
      filePath = fileUrl.replace(/^\/api\/files\//, '');
    }
    // If it's a full URL to our API
    else if (fileUrl.includes('/api/files/')) {
      try {
        const urlObj = new URL(fileUrl);
        filePath = urlObj.pathname.replace(/^\/api\/files\//, '');
      } catch (e) {
        console.error("Error parsing URL:", e);
        filePath = fileUrl.split('/').pop(); // Fallback to just the filename
      }
    }
    // If it's just a file path
    else {
      filePath = fileUrl.replace(/^\//, ''); // Remove leading slash if present
    }
    
    // Do not include token in URL - we'll use Authorization header instead
    return `${apiBaseUrl}/api/files/${filePath}`;
  };

  // Handle direct file download
  const handleDownload = async () => {
    try {
      toast.loading('Preparing download...');
      
      const fileApiUrl = getProperFileUrl();
      
      // For direct download, use fetch with authentication
      const response = await fetch(fileApiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }
      
      // Get the file data as a blob
      const blob = await response.blob();
      
      // Create a URL for the blob
      const downloadUrl = URL.createObjectURL(blob);
      
      // Create a temporary anchor element
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = title || (fileUrl ? fileUrl.split('/').pop() : 'download');
      
      // Append to the document
      document.body.appendChild(a);
      
      // Click the link
      a.click();
      
      // Clean up
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);
      
      toast.dismiss();
      toast.success('Download started');
    } catch (error) {
      console.error('Download error:', error);
      toast.dismiss();
      toast.error(`Download failed: ${error.message}`);
      setFileError(true);
    }
  };

  // Open in new tab
  const handleOpenInNewTab = () => {
    try {
      // Create a temporary anchor with download attribute
      const a = document.createElement('a');
      a.href = getProperFileUrl();
      
      // Add token as a search parameter for authentication
      // This is only for opening in a new tab where we can't set headers
      const url = new URL(a.href);
      url.searchParams.append('token', token);
      
      a.href = url.toString();
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      
      // Append to the document
      document.body.appendChild(a);
      
      // Click the link
      a.click();
      
      // Clean up
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error opening in new tab:', error);
      toast.error('Failed to open in new tab');
    }
  };

  // Fetch comments for the material
  const fetchComments = async () => {
    if (!materialId) return;
    
    try {
      const response = await fetch(`${apiBaseUrl}/api/materials/${materialId}/comments`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch comments');
      }
      
      const data = await response.json();
      setComments(data);
    } catch (error) {
      console.error('Error fetching comments:', error);
      toast.error('Failed to load comments');
    }
  };

  // Post a new comment
  const handlePostComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      setIsPostingComment(true);
      
      const commentData = {
        content: newComment.trim(),
        materialId,
        parentCommentId: replyTo ? replyTo.id : null
      };
      
      const response = await fetch(`${apiBaseUrl}/api/comments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(commentData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to post comment');
      }
      
      const newCommentData = await response.json();
      
      // Add the new comment to the state
      setComments(prevComments => [...prevComments, newCommentData]);
      
      // Clear input and reset reply state
      setNewComment('');
      setReplyTo(null);
      
      // Scroll to the new comment
      setTimeout(() => {
        commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (error) {
      console.error('Error posting comment:', error);
      toast.error('Failed to post comment');
    } finally {
      setIsPostingComment(false);
    }
  };

  // Edit a comment
  const handleEditComment = async () => {
    if (!editText.trim() || !editingComment) return;
    
    try {
      const response = await fetch(`${apiBaseUrl}/api/comments/${editingComment.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: editText.trim() })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update comment');
      }
      
      const updatedComment = await response.json();
      
      // Update the comment in the state
      setComments(prevComments => prevComments.map(comment => 
        comment.id === updatedComment.id ? updatedComment : comment
      ));
      
      // Reset editing state
      setEditingComment(null);
      setEditText('');
      
      toast.success('Comment updated');
    } catch (error) {
      console.error('Error updating comment:', error);
      toast.error('Failed to update comment');
    }
  };

  // Delete a comment
  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }
    
    try {
      const response = await fetch(`${apiBaseUrl}/api/comments/${commentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete comment');
      }
      
      // Remove the deleted comment from the state
      setComments(prevComments => prevComments.filter(comment => comment.id !== commentId));
      
      toast.success('Comment deleted');
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('Failed to delete comment');
    }
  };

  const toggleMenu = (commentId, e) => {
    e.stopPropagation(); // Prevent event bubbling
    setOpenMenuId(openMenuId === commentId ? null : commentId);
  };

  // Like a comment
  const handleLikeComment = async (commentId) => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/comments/${commentId}/like`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to like comment');
      }
      
      const updatedComment = await response.json();
      
      // Update the comment in the state
      setComments(prevComments => prevComments.map(comment => 
        comment.id === updatedComment.id ? updatedComment : comment
      ));
    } catch (error) {
      console.error('Error liking comment:', error);
      toast.error('Failed to like comment');
    }
  };

  // Report comment function
  const handleReportComment = async (commentId) => {
    const reason = prompt('Please provide a reason for reporting this comment:');
    if (!reason || !reason.trim()) return;
    
    try {
      const response = await fetch(`${apiBaseUrl}/api/comments/${commentId}/report`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: reason.trim() })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to report comment');
      }
      
      toast.success('Comment reported successfully');
      setOpenMenuId(null); // Close the menu
    } catch (error) {
      console.error('Error reporting comment:', error);
      toast.error(error.message || 'Failed to report comment');
    }
  };

  // Start replying to a comment
  const handleReply = (comment) => {
    setReplyTo(comment);
    setNewComment(`@${comment.userName} `);
    // Focus on the input
    setTimeout(() => {
      commentInputRef.current?.focus();
    }, 100);
  };

  // Start editing a comment
  const handleEdit = (comment) => {
    setEditingComment(comment);
    setEditText(comment.content);
  };

  // Cancel editing/replying
  const handleCancel = () => {
    if (editingComment) {
      setEditingComment(null);
      setEditText('');
    }
    if (replyTo) {
      setReplyTo(null);
      setNewComment('');
    }
  };

  // More robust file type detection
  // Note: Office documents (Word, Excel, PowerPoint) are now automatically converted to PDF on upload
  const getContentType = () => {
    // Check by mimetype first
    if (fileType) {
      if (fileType.startsWith('image/')) return 'image';
      if (fileType === 'application/pdf') return 'pdf';
      if (fileType === 'text/plain') return 'text';
      if (fileType === 'text/markdown' || fileType === 'text/x-markdown') return 'markdown';
      if (fileType === 'text/csv' || fileType === 'application/csv') return 'csv';
      
      // Legacy Office document types - these should now be converted to PDF
      // But keeping for backward compatibility with old uploads
      if (fileType === 'application/vnd.ms-excel' || 
          fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
        console.warn('Excel file detected - should have been converted to PDF');
        return 'excel';
      }
      if (fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        console.warn('Word file detected - should have been converted to PDF');
        return 'docx';
      }
      if (fileType === 'application/msword') {
        console.warn('Word file detected - should have been converted to PDF');
        return 'doc';
      }
      if (fileType === 'application/vnd.ms-powerpoint' || 
          fileType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') {
        console.warn('PowerPoint file detected - should have been converted to PDF');
        return 'powerpoint';
      }
      
      // Other text-based formats
      if (fileType === 'application/javascript' || fileType === 'text/javascript') return 'text';
      if (fileType === 'text/css') return 'text';
      if (fileType === 'application/json') return 'text';
      if (fileType === 'text/html') return 'text';
    }
    
    // Check by file extension if no mimetype or as a fallback
    if (!fileUrl) return 'other';
    const lowerUrl = fileUrl.toLowerCase();
    if (/\.(jpe?g|png|gif|svg|webp|bmp|tiff?)$/i.test(lowerUrl)) return 'image';
    if (/\.pdf$/i.test(lowerUrl)) return 'pdf';
    if (/\.(txt|log|yml|yaml|env|config|gitignore|htaccess)$/i.test(lowerUrl)) return 'text';
    if (/\.(md|markdown|mdown)$/i.test(lowerUrl)) return 'markdown';
    if (/\.csv$/i.test(lowerUrl)) return 'csv';
    
    // Legacy Office document extensions - these should be rare now
    if (/\.(xls|xlsx|xlsm|xlt|xltx|xltm)$/i.test(lowerUrl)) {
      console.warn('Excel file extension detected - should have been converted to PDF');
      return 'excel';
    }
    if (/\.(docx)$/i.test(lowerUrl)) {
      console.warn('Word file extension detected - should have been converted to PDF');
      return 'docx';
    }
    if (/\.(doc|dot|rtf)$/i.test(lowerUrl)) {
      console.warn('Word file extension detected - should have been converted to PDF');
      return 'doc';
    }
    if (/\.(ppt|pptx|pptm|pot|potx|potm|pps|ppsx|ppsm)$/i.test(lowerUrl)) {
      console.warn('PowerPoint file extension detected - should have been converted to PDF');
      return 'powerpoint';
    }
    
    // Code and markup files
    if (/\.(js|jsx|ts|tsx)$/i.test(lowerUrl)) return 'text';
    if (/\.css$/i.test(lowerUrl)) return 'text';
    if (/\.json$/i.test(lowerUrl)) return 'text';
    if (/\.(html?|xml|svg)$/i.test(lowerUrl)) return 'text';
    
    // Default
    return 'other';
  };

  // Fetch comments when component mounts
  useEffect(() => {
    if (materialId) {
      fetchComments();
    }
  }, [materialId]);

  // Scroll to bottom when a new comment is added
  useEffect(() => {
    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [comments.length]);

  useEffect(() => {
    const handleClickOutside = () => {
      if (openMenuId) {
        setOpenMenuId(null);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [openMenuId]);
  
  // Render different content based on file type
  const renderContent = () => {
    const contentType = getContentType();
    const fileApiUrl = getProperFileUrl();
    
    // All viewer components get same props
    const viewerProps = {
      fileUrl: fileApiUrl,
      token,
      zoom,
      rotation,
      setZoom,
      setRotation,
      onError: () => setFileError(true)
    };
    
    switch (contentType) {
      case 'image':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <ImageViewer {...viewerProps} title={title} />
          </Suspense>
        );
        
      case 'pdf':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <PdfViewer {...viewerProps} />
          </Suspense>
        );
        
      case 'text':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <TextViewer {...viewerProps} />
          </Suspense>
        );
        
      case 'markdown':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <MarkdownViewer {...viewerProps} />
          </Suspense>
        );
        
      case 'csv':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <CsvViewer {...viewerProps} />
          </Suspense>
        );

      case 'docx':
        return (
          <Suspense fallback={<LoadingFallback />}>
            <DocxViewer {...viewerProps} title={title} />
          </Suspense>
        );
        
      case 'excel':
      case 'word':
      case 'doc':
          // Legacy Office documents - these should have been converted to PDF
          return (
            <div className="flex flex-col items-center justify-center h-full text-gray-600 dark:text-gray-400">
              <div className="p-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg text-center mb-6 max-w-md">
                <FileText className="h-16 w-16 mx-auto mb-4 text-yellow-600" />
                <h3 className="text-xl font-semibold mb-2 text-yellow-800 dark:text-yellow-200">{title}</h3>
                <p className="text-sm mb-4 text-yellow-700 dark:text-yellow-300">
                  {contentType === 'excel' ? 'Excel spreadsheet' : 'Word document'}
                </p>
                <p className="text-sm mb-4 text-yellow-700 dark:text-yellow-300">
                  <strong>Legacy Format:</strong> This document was uploaded before automatic PDF conversion was enabled.
                </p>
                <p className="text-sm mb-6 text-yellow-600 dark:text-yellow-400">
                  New uploads of this file type will be automatically converted to PDF for better viewing and text extraction.
                </p>
                
                <div className="flex space-x-4 justify-center">
                  <button
                    onClick={handleOpenInNewTab}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center"
                  >
                    <ExternalLink className="mr-2 h-5 w-5" />
                    View in New Tab
                  </button>
                  
                  <button
                    onClick={handleDownload}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors flex items-center"
                  >
                    <Download className="mr-2 h-5 w-5" />
                    Download
                  </button>
                </div>
              </div>
            </div>
          );
      case 'powerpoint':
        return (
          <div className="flex flex-col items-center justify-center h-full text-gray-600 dark:text-gray-400">
            <div className="p-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg text-center mb-6 max-w-md">
              <FileText className="h-16 w-16 mx-auto mb-4 text-yellow-600" />
              <h3 className="text-xl font-semibold mb-2 text-yellow-800 dark:text-yellow-200">{title}</h3>
              <p className="text-sm mb-4 text-yellow-700 dark:text-yellow-300">PowerPoint presentation</p>
              <p className="text-sm mb-4 text-yellow-700 dark:text-yellow-300">
                <strong>Legacy Format:</strong> This presentation was uploaded before automatic PDF conversion was enabled.
              </p>
              <p className="text-sm mb-6 text-yellow-600 dark:text-yellow-400">
                New uploads of this file type will be automatically converted to PDF for better viewing and text extraction.
              </p>
              
              <div className="flex space-x-4 justify-center">
                <button
                  onClick={handleOpenInNewTab}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center"
                >
                  <ExternalLink className="mr-2 h-5 w-5" />
                  View in New Tab
                </button>
                
                <button
                  onClick={handleDownload}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors flex items-center"
                >
                  <Download className="mr-2 h-5 w-5" />
                  Download
                </button>
              </div>
            </div>
          </div>
        );
        
      default:
        return (
          <div className="flex flex-col items-center justify-center h-full text-gray-600 dark:text-gray-400">
            <div className="p-6 bg-gray-100 dark:bg-gray-800 rounded-lg text-center mb-6 max-w-md">
              <FileText className="h-16 w-16 mx-auto mb-4 text-gray-500" />
              <h3 className="text-xl font-semibold mb-2">{title}</h3>
              <p className="text-sm mb-4">This file type cannot be previewed</p>
              
              <button
                onClick={handleDownload}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors flex items-center mx-auto"
              >
                <Download className="mr-2 h-5 w-5" />
                Download File
              </button>
            </div>
          </div>
        );
    }
  };

  // Render comments section
  const renderComments = () => {
    if (!showComments) return null;
    
    return (
      <div className="w-80 md:w-96 h-full flex flex-col bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-700">
        {/* Comments header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Comments</h3>
          <button
            onClick={() => setShowComments(false)}
            className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded"
          >
            <X size={16} />
          </button>
        </div>
        
        {/* Comments list */}
        <div className="flex-grow overflow-y-auto p-4 space-y-4">
          {comments.length === 0 ? (
            <div className="text-center text-gray-500 dark:text-gray-400 py-8">
              <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-30" />
              <p>No comments yet. Be the first to comment!</p>
            </div>
          ) : (
            comments.map(comment => (
              <div 
                key={comment.id} 
                className={`${
                  comment.parentCommentId 
                    ? 'ml-6 pl-4 border-l-2 border-gray-200 dark:border-gray-700' 
                    : ''
                }`}
              >
                {editingComment && editingComment.id === comment.id ? (
                  // Edit comment form
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                    <textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      rows={3}
                    />
                    <div className="flex justify-end space-x-2 mt-2">
                      <button
                        onClick={handleCancel}
                        className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleEditComment}
                        className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  // Comment display
                  <div>
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <ProfilePicture 
                          url={comment.userProfilePictureUrl} 
                          size="sm"
                          className="h-8 w-8"
                        />
                      </div>
                      <div className="flex-grow">
                        <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-3">
                          <div className="flex justify-between items-start">
                            <div className="font-medium text-gray-900 dark:text-white">
                              <Link 
                                to={`/users/${comment.userId}`}
                                className="hover:text-red-600 dark:hover:text-red-400"
                              >
                                {comment.userName}
                              </Link>
                            </div>
                            <div className="relative">
                              {/* SHOW DIRECT DELETE BUTTON FOR MODERATORS/ADMINS */}
                              {canModerate && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteComment(comment.id);
                                  }}
                                  className="p-1 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 rounded"
                                  title="Delete comment (Moderator)"
                                >
                                  <Trash size={14} />
                                </button>
                              )}
                              
                              {/* MENU BUTTON - show if user owns comment OR if they can report */}
                              {(currentUser && (comment.userId === currentUser.id || comment.userId !== currentUser.id)) && (
                                <button
                                  onClick={(e) => toggleMenu(comment.id, e)}
                                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded ml-1"
                                >
                                  <MoreHorizontal size={14} />
                                </button>
                              )}
                              
                              {openMenuId === comment.id && (
                                <div className="absolute right-0 mt-1 w-36 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-[9999]">
                                  {/* Edit button - only for comment author */}
                                  {currentUser && comment.userId === currentUser.id && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEdit(comment);
                                        setOpenMenuId(null);
                                      }}
                                      className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                                    >
                                      <Edit size={14} className="mr-2" />
                                      Edit
                                    </button>
                                  )}
                                  
                                  {/* Delete button - only for comment author (non-moderators) */}
                                  {currentUser && comment.userId === currentUser.id && !canModerate && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteComment(comment.id);
                                        setOpenMenuId(null);
                                      }}
                                      className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                                    >
                                      <Trash size={14} className="mr-2" />
                                      Delete
                                    </button>
                                  )}
                                  
                                  {/* Report button - show for everyone except comment author */}
                                  {currentUser && comment.userId !== currentUser.id && (
                                    <>
                                      {/* Separator if there are other buttons above */}
                                      {currentUser && comment.userId === currentUser.id && (
                                        <hr className="my-1 border-gray-200 dark:border-gray-600" />
                                      )}
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleReportComment(comment.id);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-yellow-600 dark:text-yellow-400 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                                      >
                                        <Flag size={14} className="mr-2" />
                                        Report
                                      </button>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <p className="text-gray-700 dark:text-gray-300 mt-1 text-sm">
                            {comment.content}
                          </p>
                        </div>
                        <div className="flex items-center mt-1 space-x-4 text-xs text-gray-500 dark:text-gray-400">
                          <span>
                            {new Date(comment.createdAt).toLocaleDateString(undefined, {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </span>
                          <button
                            onClick={() => handleLikeComment(comment.id)}
                            className="flex items-center hover:text-red-500"
                          >
                            <Heart
                              size={12}
                              className={`mr-1 ${
                                comment.userHasLiked ? 'fill-red-500 text-red-500' : ''
                              }`}
                            />
                            <span>{comment.likes || 0}</span>
                          </button>
                          <button
                            onClick={() => handleReply(comment)}
                            className="flex items-center hover:text-blue-500"
                          >
                            <Reply size={12} className="mr-1" />
                            Reply
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
          <div ref={commentsEndRef} />
        </div>
        
        {/* Reply indicator */}
        {replyTo && (
          <div className="px-4 py-2 bg-gray-100 dark:bg-gray-800 flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Replying to <span className="font-medium">{replyTo.userName}</span>
            </div>
            <button
              onClick={handleCancel}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              <X size={16} />
            </button>
          </div>
        )}
        
        {/* Comment input */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-3">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <ProfilePicture 
                url={currentUser?.profilePictureUrl} 
                size="sm"
                className="h-8 w-8"
              />
            </div>
            <div className="flex-grow relative">
              <textarea
                ref={commentInputRef}
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                className="w-full p-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                rows={1}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handlePostComment();
                  }
                }}
              />
              <button
                onClick={handlePostComment}
                disabled={isPostingComment || !newComment.trim()}
                className={`absolute right-2 bottom-2 p-1 rounded-full ${
                  isPostingComment || !newComment.trim()
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-red-500 hover:bg-red-100 dark:hover:bg-red-900'
                }`}
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Modal/Container class based on fullscreen state and usage context
  const getContainerClass = () => {
    if (isFullScreenPage) {
      // When used in full-screen page, fill the entire container
      return 'h-full flex flex-col bg-white dark:bg-gray-900';
    }
    // Original modal classes for popup usage
    return isFullScreen
      ? 'fixed inset-0 z-[100] flex flex-col bg-white dark:bg-gray-900' 
      : 'fixed left-4 right-4 top-4 bottom-4 z-[100] flex flex-col bg-white dark:bg-gray-900 rounded-lg shadow-2xl';
  };

  // Display error fallback if something went wrong
  if (fileError) {
    return (
      <div className="fixed inset-0 z-40 overflow-hidden">
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black bg-opacity-75 transition-opacity" 
          onClick={onClose}
        />
        
        {/* Error modal */}
        <div className={getContainerClass()}>
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white truncate">{title}</h3>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded"
            >
              <X size={20} />
            </button>
          </div>
          
          <div className="flex-grow flex items-center justify-center">
            <div className="text-center text-gray-600 dark:text-gray-400 p-6">
              <FileText className="h-16 w-16 mx-auto mb-4 text-red-500" />
              <h3 className="text-xl font-semibold mb-2">File Preview Error</h3>
              <p className="mb-6">We couldn't preview this file. This might be due to an authentication issue or unsupported file format.</p>
              
              <div className="flex space-x-4 justify-center">
                <button
                  onClick={handleOpenInNewTab}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center"
                >
                  <ExternalLink className="mr-2 h-5 w-5" />
                  Try Opening in New Tab
                </button>
                
                <button
                  onClick={handleDownload}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors flex items-center"
                >
                  <Download className="mr-2 h-5 w-5" />
                  Download Instead
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render wrapper - different for full-screen page vs modal
  const renderWrapper = (children) => {
    if (isFullScreenPage) {
      return <div className={getContainerClass()}>{children}</div>;
    }
    
    return (
      <div className="fixed inset-0 z-[90] overflow-hidden">
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black bg-opacity-75 transition-opacity" 
          onClick={onClose}
        />
        
        {/* Document viewer modal */}
        <div className={getContainerClass()}>
          {children}
        </div>
      </div>
    );
  };

  return renderWrapper(
    <>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white truncate">{title}</h3>
          <div className="flex items-center space-x-2">
            {/* Toggle comments button */}
            {materialId && (
              <button
                onClick={() => setShowComments(!showComments)}
                className={`p-2 rounded ${
                  showComments 
                    ? 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400' 
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                <MessageSquare size={20} />
              </button>
            )}
            
            <button
              onClick={toggleFullScreen}
              className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded"
            >
              {isFullScreen ? <Minimize size={20} /> : <Maximize size={20} />}
            </button>
            <button
              onClick={handleDownload}
              className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded"
            >
              <Download size={20} />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded"
            >
              <X size={20} />
            </button>
          </div>
        </div>
        
        {/* Toolbar - show for PDFs and images */}
        {(['image', 'pdf'].includes(getContentType())) && (
          <div className="flex items-center justify-center space-x-2 p-2 bg-gray-100 dark:bg-gray-800">
            <button
              onClick={() => handleZoom('out')}
              className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded"
              disabled={zoom <= 0.5}
            >
              <ZoomOut size={16} />
            </button>
            <span className="text-sm text-gray-600 dark:text-gray-400">{Math.round(zoom * 100)}%</span>
            <button
              onClick={() => handleZoom('in')}
              className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded"
              disabled={zoom >= 2}
            >
              <ZoomIn size={16} />
            </button>
            <button
              onClick={handleRotate}
              className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded"
            >
              <RotateCw size={16} />
            </button>
          </div>
        )}
        
        {/* Content area */}
        <div className="flex-grow overflow-hidden relative">
          <div className="flex h-full">
            {/* Document content */}
            <div className={`h-full ${showComments ? 'flex-grow' : 'w-full'} overflow-auto`}>
              {renderContent()}
            </div>
            
            {/* Comments panel */}
            {renderComments()}
          </div>
        </div>
    </>
  );
};

export default DocumentViewer;